/**
 * ChatHistoryHelper
 * 
 * Static utility methods for chat history parsing and formatting operations.
 * 
 * Methods:
 * 1. parseChatResponse() - Parse chat response into structured objects
 * 2. generateGuid() - Generate unique identifier for chat sessions
 */

import { v4 as uuidv4 } from 'uuid';

export class ChatHistoryHelper {
  
  /**
   * Parse chat response array into structured response objects
   */
  static parseChatResponse(ChatResponse: any): {
    AgentPayload?: any;
    GetMcode?: any;
    getSnapshotConfiguration?: any;
    GetCustomSnapshotWidget?: any;
    GetQuery?: any;
    getSnapshotOutput?: any;
    getSnapshotLoadData?: any;
    getConfiguration?: any;
    getSnapshotInsights?: any;
    getInsights?: any;
    getQnA?: any;
  } {
    const parsedResponses: Record<string, any> = {};

    if (!Array.isArray(ChatResponse)) {
      return {};
    }
    ChatResponse.forEach((item: any, index: any) => {
      if (typeof item !== 'object' || item === null) return;

      Object.keys(item).forEach((key) => {
        const value = item[key];
        if (typeof value === 'string') {
          try {
            // Only try to parse if it looks like JSON (starts with { or [)
            const trimmedValue = value.trim();
            if ((trimmedValue.startsWith('{') && trimmedValue.endsWith('}')) ||
              (trimmedValue.startsWith('[') && trimmedValue.endsWith(']'))) {
              parsedResponses[key] = JSON.parse(value);
            } else {
              parsedResponses[key] = value;
            }
          } catch (e) {
            parsedResponses[key] = value; // Keep original value if parsing fails
          }
        } else {
          parsedResponses[key] = value;
        }
      });
    });

    return {
      AgentPayload: parsedResponses['AgentPayload'],
      GetMcode: parsedResponses['GetMcode'],
      getSnapshotConfiguration: parsedResponses['getSnapshotConfiguration'],
      GetCustomSnapshotWidget: parsedResponses['GetCustomSnapshotWidget'],
      GetQuery: parsedResponses['GetQuery'],
      getSnapshotOutput: parsedResponses['getSnapshotOutput'],
      getSnapshotLoadData: parsedResponses['getSnapshotLoadData'],
      getConfiguration: parsedResponses['getConfiguration'],
      getSnapshotInsights: parsedResponses['getSnapshotInsights'],
      getInsights: parsedResponses['getInsights'],
      getQnA: parsedResponses['getQnA'],
    };
  }

  /**
   * Generate unique identifier for chat sessions
   */
  static generateGuid(): string {
    return uuidv4();
  }

  /**
   * Helper method to format chat response entry for storage
   * Used for consistent logging and storage formatting
   */
  static formatChatResponseEntry(key: string, value: any): { [key: string]: string } {
    const stringValue = typeof value === 'string' ? value : JSON.stringify(value);
    console.log(`[DEBUG] Saving to chat response - Key: ${key}, Value length: ${stringValue.length}, Preview: ${stringValue.substring(0, 100)}...`);
    return { [key]: stringValue };
  }

  /**
   * Helper method to sort chat history by message index
   * Returns sorted array without modifying original
   */
  static sortChatHistoryByIndex(history: any[]): any[] {
    if (!history || history.length === 0) return [];
    
    return [...history].sort((a, b) => {
      const indexA = a.MessageIndex !== undefined ? a.MessageIndex : 0;
      const indexB = b.MessageIndex !== undefined ? b.MessageIndex : 0;
      return indexA - indexB;
    });
  }

  /**
   * Helper method to filter chat list by search tokens
   * Returns filtered chats based on title matching
   */
  static filterChatsBySearch(
    allChats: any[],
    searchText: string
  ): any[] {
    const searchTokens = searchText.toLowerCase().trim().split(/\s+/).filter(token => token.length > 0);
    
    if (searchTokens.length === 0) {
      return allChats;
    }

    return allChats.filter(chat => {
      const title = (chat.ChatTitle || '').toLowerCase();
      return searchTokens.every(token => title.includes(token));
    });
  }

  /**
   * Helper method to map chat list to accordion format
   * Returns structured accordion data for UI display
   */
  static mapChatsToAccordions(chats: any[]): any[] {
    return chats.map(chat => {
      const firstQuestion = (chat.ChatHistory || []).find((h: any) => h.MessageIndex === 0);
      const history = (chat.ChatHistory || []);
      const headerTitle = firstQuestion ? firstQuestion.Question : chat.ChatTitle;

      return {
        mainQuestion: headerTitle,
        mainQuestionMessageId: firstQuestion?.MessageId || null,
        chatId: chat.ChatId,
        questions: history,
        isExpanded: false
      };
    });
  }

  /**
   * Helper method to create chat preview block
   * Returns formatted chat preview object for UI
   */
  static createChatPreviewBlock(
    message: any,
    defaultQuestionAnswer: string
  ): any {
    return {
      question: message.Question || '',
      response: message.QuestionAnswer || '',
      messageId: message.MessageId,
      isStarred: message.IsStarred || false,
      isLiked: message.IsLiked ?? null,
      feedbackSaved: (message.IsLiked === true || message.IsLiked === false),
      showDislikeInput: false,
      dislikeReason: message.DislikeReason || '',
      QuestionAnswer: message.QuestionAnswer || defaultQuestionAnswer
    };
  }

  /**
   * Helper method to sort chats by creation date
   * Returns sorted array with most recent first
   */
  static sortChatsByDate(chats: any[]): any[] {
    return [...chats].sort((a, b) => {
      const dateA = a.CreatedAt ? new Date(a.CreatedAt).getTime() : 0;
      const dateB = b.CreatedAt ? new Date(b.CreatedAt).getTime() : 0;
      return dateB - dateA; // Most recent first
    });
  }
}
